/**
 * PaginaOrganizacao - Página de gerenciamento de organizações
 *
 * CONTEXTO DE NEGÓCIO:
 * Permite que usuários gerenciem suas organizações (escritórios de advocacia):
 * - Ver organizações das quais é membro
 * - Criar nova organização
 * - Gerenciar membros (convidar, remover, alterar roles)
 * - Ver e cancelar convites pendentes
 * - Sair de uma organização
 * - Transferir propriedade (se owner)
 *
 * PERMISSÕES:
 * - Member: visualizar membros
 * - Admin: convidar membros, alterar roles de members, remover members
 * - Owner: todas as permissões + promover admins, remover admins, transferir ownership
 */

import React, { useState, useEffect } from 'react';
import { useOrganizacao } from '../contextos/ContextoOrganizacao';
import { useAutenticacao } from '../contextos/ContextoAutenticacao';
import {
  listarMembros,
  listarConvitesPendentes,
  criarConvite,
  cancelarConvite,
  alterarRoleMembro,
  removerMembro,
  transferirOwnership,
} from '../servicos/servicoOrganizacao';
import type {
  MembroOrganizacao,
  ConviteOrganizacao,
  DadosEnvioConvite,
} from '../tipos/tiposOrganizacao';
import { ehAdministrador, ehOwner, labelRole } from '../tipos/tiposOrganizacao';

// ===== Componente Principal =====

export const PaginaOrganizacao: React.FC = () => {
  const { usuario } = useAutenticacao();
  const {
    organizacaoAtual,
    organizacoes,
    roleAtual,
    carregando: carregandoOrgs,
    selecionarOrganizacao,
    criarOrganizacao,
    sairDaOrganizacao,
  } = useOrganizacao();

  // Estados locais
  const [membros, setMembros] = useState<MembroOrganizacao[]>([]);
  const [convites, setConvites] = useState<ConviteOrganizacao[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);

  // Estados de formulários
  const [mostrarFormCriar, setMostrarFormCriar] = useState(false);
  const [novaOrgNome, setNovaOrgNome] = useState('');
  const [novaOrgDescricao, setNovaOrgDescricao] = useState('');

  const [mostrarFormConvite, setMostrarFormConvite] = useState(false);
  const [conviteEmail, setConviteEmail] = useState('');
  const [conviteRole, setConviteRole] = useState<'member' | 'admin'>('member');

  const [mostrarFormTransferir, setMostrarFormTransferir] = useState(false);
  const [novoOwnerId, setNovoOwnerId] = useState('');

  // Carregar membros e convites quando organização mudar
  useEffect(() => {
    if (organizacaoAtual) {
      carregarDadosOrganizacao();
    } else {
      setMembros([]);
      setConvites([]);
    }
  }, [organizacaoAtual]);

  async function carregarDadosOrganizacao() {
    if (!organizacaoAtual) return;

    setCarregando(true);
    setErro(null);
    try {
      const [membrosList, convitesList] = await Promise.all([
        listarMembros(organizacaoAtual.id),
        ehAdministrador(roleAtual) ? listarConvitesPendentes(organizacaoAtual.id) : Promise.resolve([]),
      ]);
      setMembros(membrosList);
      setConvites(convitesList);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao carregar dados');
    } finally {
      setCarregando(false);
    }
  }

  // ===== Handlers =====

  async function handleCriarOrganizacao(e: React.FormEvent) {
    e.preventDefault();
    if (!novaOrgNome.trim()) return;

    setCarregando(true);
    setErro(null);
    try {
      await criarOrganizacao({
        nome: novaOrgNome.trim(),
        descricao: novaOrgDescricao.trim() || undefined,
      });
      setSucesso('Organização criada com sucesso!');
      setMostrarFormCriar(false);
      setNovaOrgNome('');
      setNovaOrgDescricao('');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao criar organização');
    } finally {
      setCarregando(false);
    }
  }

  async function handleEnviarConvite(e: React.FormEvent) {
    e.preventDefault();
    if (!organizacaoAtual || !conviteEmail.trim()) return;

    setCarregando(true);
    setErro(null);
    try {
      const dados: DadosEnvioConvite = {
        email: conviteEmail.trim().toLowerCase(),
        role: conviteRole,
      };
      const novoConvite = await criarConvite(organizacaoAtual.id, dados);
      setConvites((prev: ConviteOrganizacao[]) => [novoConvite, ...prev]);
      setSucesso(`Convite enviado para ${conviteEmail}`);
      setMostrarFormConvite(false);
      setConviteEmail('');
      setConviteRole('member');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao enviar convite');
    } finally {
      setCarregando(false);
    }
  }

  async function handleCancelarConvite(conviteId: string) {
    if (!organizacaoAtual) return;

    setCarregando(true);
    setErro(null);
    try {
      await cancelarConvite(organizacaoAtual.id, conviteId);
      setConvites((prev: ConviteOrganizacao[]) => prev.filter((c: ConviteOrganizacao) => c.id !== conviteId));
      setSucesso('Convite cancelado');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao cancelar convite');
    } finally {
      setCarregando(false);
    }
  }

  async function handleAlterarRole(usuarioId: string, novoRole: 'admin' | 'member') {
    if (!organizacaoAtual) return;

    setCarregando(true);
    setErro(null);
    try {
      await alterarRoleMembro(organizacaoAtual.id, usuarioId, { novo_role: novoRole });
      setMembros((prev: MembroOrganizacao[]) =>
        prev.map((m: MembroOrganizacao) => (m.usuario_id === usuarioId ? { ...m, role: novoRole } : m))
      );
      setSucesso('Role alterado com sucesso');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao alterar role');
    } finally {
      setCarregando(false);
    }
  }

  async function handleRemoverMembro(usuarioId: string) {
    if (!organizacaoAtual) return;

    if (!window.confirm('Tem certeza que deseja remover este membro?')) return;

    setCarregando(true);
    setErro(null);
    try {
      await removerMembro(organizacaoAtual.id, usuarioId);
      setMembros((prev: MembroOrganizacao[]) => prev.filter((m: MembroOrganizacao) => m.usuario_id !== usuarioId));
      setSucesso('Membro removido');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao remover membro');
    } finally {
      setCarregando(false);
    }
  }

  async function handleSairDaOrganizacao() {
    if (!organizacaoAtual) return;

    if (!window.confirm('Tem certeza que deseja sair desta organização?')) return;

    setCarregando(true);
    setErro(null);
    try {
      await sairDaOrganizacao(organizacaoAtual.id);
      setSucesso('Você saiu da organização');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao sair da organização');
    } finally {
      setCarregando(false);
    }
  }

  async function handleTransferirOwnership(e: React.FormEvent) {
    e.preventDefault();
    if (!organizacaoAtual || !novoOwnerId) return;

    if (!window.confirm('Tem certeza que deseja transferir a propriedade? Você se tornará administrador.')) return;

    setCarregando(true);
    setErro(null);
    try {
      await transferirOwnership(organizacaoAtual.id, { novo_owner_id: novoOwnerId });
      setSucesso('Propriedade transferida com sucesso');
      setMostrarFormTransferir(false);
      setNovoOwnerId('');
      await carregarDadosOrganizacao();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao transferir propriedade');
    } finally {
      setCarregando(false);
    }
  }

  // ===== Render =====

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Organizações</h1>

      {/* Mensagens */}
      {erro && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {erro}
          <button onClick={() => setErro(null)} className="float-right font-bold">×</button>
        </div>
      )}
      {sucesso && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {sucesso}
          <button onClick={() => setSucesso(null)} className="float-right font-bold">×</button>
        </div>
      )}

      {/* Seletor de Organização */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-4">
          <label className="font-medium">Organização ativa:</label>
          <select
            value={organizacaoAtual?.id || ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => selecionarOrganizacao(e.target.value || null)}
            className="flex-1 p-2 border rounded"
            disabled={carregandoOrgs}
          >
            <option value="">Nenhuma (modo individual)</option>
            {organizacoes.map((org) => (
              <option key={org.id} value={org.id}>
                {org.nome} ({labelRole(org.meu_role || 'member')})
              </option>
            ))}
          </select>
          <button
            onClick={() => setMostrarFormCriar(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Nova Organização
          </button>
        </div>
      </div>

      {/* Formulário Criar Organização */}
      {mostrarFormCriar && (
        <div className="mb-6 p-4 bg-white border rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Criar Nova Organização</h2>
          <form onSubmit={handleCriarOrganizacao} className="space-y-4">
            <div>
              <label className="block font-medium mb-1">Nome *</label>
              <input
                type="text"
                value={novaOrgNome}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNovaOrgNome(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Ex: Escritório Silva & Associados"
                required
              />
            </div>
            <div>
              <label className="block font-medium mb-1">Descrição</label>
              <textarea
                value={novaOrgDescricao}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNovaOrgDescricao(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Descrição opcional"
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={carregando}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {carregando ? 'Criando...' : 'Criar'}
              </button>
              <button
                type="button"
                onClick={() => setMostrarFormCriar(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Detalhes da Organização Atual */}
      {organizacaoAtual && (
        <div className="space-y-6">
          {/* Info da Organização */}
          <div className="p-4 bg-white border rounded-lg shadow">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-semibold">{organizacaoAtual.nome}</h2>
                {organizacaoAtual.descricao && (
                  <p className="text-gray-600 mt-1">{organizacaoAtual.descricao}</p>
                )}
                <p className="text-sm text-gray-500 mt-2">
                  Seu role: <span className="font-medium">{labelRole(roleAtual || 'member')}</span>
                </p>
              </div>
              <div className="flex gap-2">
                {!ehOwner(roleAtual) && (
                  <button
                    onClick={handleSairDaOrganizacao}
                    className="px-3 py-1 text-red-600 border border-red-600 rounded hover:bg-red-50"
                  >
                    Sair
                  </button>
                )}
                {ehOwner(roleAtual) && (
                  <button
                    onClick={() => setMostrarFormTransferir(true)}
                    className="px-3 py-1 text-orange-600 border border-orange-600 rounded hover:bg-orange-50"
                  >
                    Transferir Propriedade
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Formulário Transferir Ownership */}
          {mostrarFormTransferir && ehOwner(roleAtual) && (
            <div className="p-4 bg-white border rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Transferir Propriedade</h3>
              <form onSubmit={handleTransferirOwnership} className="space-y-4">
                <div>
                  <label className="block font-medium mb-1">Novo Proprietário</label>
                  <select
                    value={novoOwnerId}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setNovoOwnerId(e.target.value)}
                    className="w-full p-2 border rounded"
                    required
                  >
                    <option value="">Selecione um membro</option>
                    {membros
                      .filter((m: MembroOrganizacao) => m.usuario_id !== usuario?.id)
                      .map((m: MembroOrganizacao) => (
                        <option key={m.usuario_id} value={m.usuario_id}>
                          {m.nome_usuario} ({m.email_usuario})
                        </option>
                      ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={carregando || !novoOwnerId}
                    className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
                  >
                    {carregando ? 'Transferindo...' : 'Transferir'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMostrarFormTransferir(false)}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Membros */}
          <div className="p-4 bg-white border rounded-lg shadow">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Membros ({membros.length})</h3>
              {ehAdministrador(roleAtual) && (
                <button
                  onClick={() => setMostrarFormConvite(true)}
                  className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Convidar
                </button>
              )}
            </div>

            {/* Formulário Convidar */}
            {mostrarFormConvite && ehAdministrador(roleAtual) && (
              <form onSubmit={handleEnviarConvite} className="mb-4 p-3 bg-gray-50 rounded space-y-3">
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={conviteEmail}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConviteEmail(e.target.value)}
                    className="flex-1 p-2 border rounded"
                    placeholder="email@exemplo.com"
                    required
                  />
                  <select
                    value={conviteRole}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setConviteRole(e.target.value as 'member' | 'admin')}
                    className="p-2 border rounded"
                    disabled={!ehOwner(roleAtual)}
                  >
                    <option value="member">Membro</option>
                    {ehOwner(roleAtual) && <option value="admin">Administrador</option>}
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={carregando}
                    className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {carregando ? 'Enviando...' : 'Enviar Convite'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMostrarFormConvite(false)}
                    className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            )}

            {/* Lista de Membros */}
            <div className="divide-y">
              {membros.map((membro: MembroOrganizacao) => (
                <div key={membro.id} className="py-3 flex justify-between items-center">
                  <div>
                    <p className="font-medium">{membro.nome_usuario}</p>
                    <p className="text-sm text-gray-600">{membro.email_usuario}</p>
                    <p className="text-xs text-gray-500">
                      {labelRole(membro.role)}
                      {membro.convidado_por && ` • Convidado por ${membro.nome_convidado_por}`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {/* Botões de ação - apenas para admins/owners e não para o próprio usuário */}
                    {ehAdministrador(roleAtual) && membro.usuario_id !== usuario?.id && membro.role !== 'owner' && (
                      <>
                        {/* Alterar role */}
                        {(ehOwner(roleAtual) || membro.role === 'member') && (
                          <select
                            value={membro.role}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleAlterarRole(membro.usuario_id, e.target.value as 'admin' | 'member')}
                            className="text-sm p-1 border rounded"
                            disabled={carregando}
                          >
                            <option value="member">Membro</option>
                            {ehOwner(roleAtual) && <option value="admin">Admin</option>}
                          </select>
                        )}
                        {/* Remover */}
                        {(ehOwner(roleAtual) || membro.role === 'member') && (
                          <button
                            onClick={() => handleRemoverMembro(membro.usuario_id)}
                            className="text-sm px-2 py-1 text-red-600 border border-red-600 rounded hover:bg-red-50"
                            disabled={carregando}
                          >
                            Remover
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Convites Pendentes */}
          {ehAdministrador(roleAtual) && convites.length > 0 && (
            <div className="p-4 bg-white border rounded-lg shadow">
              <h3 className="text-xl font-semibold mb-4">Convites Pendentes ({convites.length})</h3>
              <div className="divide-y">
                {convites.map((convite: ConviteOrganizacao) => (
                  <div key={convite.id} className="py-3 flex justify-between items-center">
                    <div>
                      <p className="font-medium">{convite.email}</p>
                      <p className="text-sm text-gray-600">
                        Role: {labelRole(convite.role_atribuido)} • Expira em:{' '}
                        {new Date(convite.expira_em).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleCancelarConvite(convite.id)}
                      className="text-sm px-2 py-1 text-red-600 border border-red-600 rounded hover:bg-red-50"
                      disabled={carregando}
                    >
                      Cancelar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mensagem quando não tem organização */}
      {!organizacaoAtual && !mostrarFormCriar && (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg mb-2">Você não está em nenhuma organização.</p>
          <p>Crie uma nova organização ou aguarde um convite para participar de uma.</p>
        </div>
      )}
    </div>
  );
};

export default PaginaOrganizacao;
